import { useEffect, useState } from 'react';
import { collection, onSnapshot } from 'firebase/firestore';
import { db } from '@/lib/firebase';
import { usePermissions } from '@/auth/usePermissions';

export interface PersonLite {
  id: string;
  firstName: string;
  lastName: string;
  displayName: string;
  fatherName: string;
  tribe: 'kohen' | 'levi' | 'israel';
  minyanId: string | null;
  active: boolean;
  isMember: boolean;
}

/**
 * The whole (small, ~200 docs) people list, live and offline-cached. Client-side filtering is the
 * design choice for this dataset (SPEC §15). Empty when the user cannot read people.
 */
export function usePeople(): { people: PersonLite[]; loading: boolean } {
  const { canRead } = usePermissions();
  const allowed = canRead('people');
  const [state, setState] = useState<{ people: PersonLite[]; loading: boolean }>({ people: [], loading: allowed });

  useEffect(() => {
    if (!allowed) { setState({ people: [], loading: false }); return; }
    return onSnapshot(collection(db, 'people'), (snap) => {
      const people = snap.docs
        .filter((d) => d.get('deleted') !== true)
        .map((d) => {
          const x = d.data();
          return {
            id: d.id, firstName: x.firstName ?? '', lastName: x.lastName ?? '',
            displayName: x.displayName ?? `${x.lastName ?? ''} ${x.firstName ?? ''}`.trim(),
            fatherName: x.fatherName ?? '', tribe: x.tribe ?? 'israel', minyanId: x.minyanId ?? null,
            active: x.active !== false, isMember: x.isMember === true,
          } satisfies PersonLite;
        })
        .sort((a, b) => a.displayName.localeCompare(b.displayName, 'he'));
      setState({ people, loading: false });
    }, () => setState({ people: [], loading: false }));
  }, [allowed]);

  return state;
}
